import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LangProvider } from "../contexts/LangContext";
import { ProfilePage } from "../pages/ProfilePage";
import { buildCompleteHead } from "../utils/seo";
import { storage } from "../lib/storage";

export const Route = createFileRoute("/$lang/profile")({
  head: ({ params }) => {
    const currentPath = `/${params.lang}/profile`;
    const canonicalUrl = `https://lotin.uz${currentPath}`;
    const langForSEO = params.lang === "en" ? "default" : params.lang;
    const seoData = buildCompleteHead(
      langForSEO as "oz" | "uz" | "ru",
      "home",
      canonicalUrl,
      currentPath,
      currentPath,
    );

    return {
      meta: [{ title: seoData.title }, ...seoData.meta],
      links: seoData.links,
    };
  },
  // Cast as any to satisfy older `beforeLoad` typing that expects `never`
  beforeLoad: (() => {
    // Check if user is authenticated (only runs on client side)
    if (typeof window !== "undefined") {
      const token = storage.getItem("auth_token");
      if (!token) {
        throw redirect({ to: "/" });
      }
    }
  }) as any,
  component: Profile,
});

function Profile() {
  const { lang } = useParams({ from: "/$lang/profile" });
  const { i18n } = useTranslation();

  useEffect(() => {
    const langMap: Record<string, string> = {
      oz: "uz-Latn", // Uzbek Latin (default)
      uz: "uz-Cyrl", // Uzbek Cyrillic
      ru: "ru", // Russian
      en: "en-US", // English
    };
    document.documentElement.lang = langMap[lang] || "uz-Latn";

    // Guard for SSR: only call changeLanguage if it exists
    if (i18n?.changeLanguage) {
      i18n.changeLanguage(lang);
    }

    // Set document title based on language
    const titleMap: Record<string, string> = {
      oz: "Profil — Lotin.uz",
      uz: "Профил — Lotin.uz",
      ru: "Профиль — Lotin.uz",
      en: "Profile — Lotin.uz",
    };
    document.title = titleMap[lang] || titleMap.oz;
  }, [lang, i18n]);

  const langMapForContext: Record<string, string> = {
    oz: "uz-Latn",
    uz: "uz-Cyrl",
    ru: "ru",
    en: "en-US",
  };

  return (
    <LangProvider lang={langMapForContext[lang] || "uz-Latn"}>
      <ProfilePage />
    </LangProvider>
  );
}
